import React, { useState } from 'react';
import { Button, Popover, Checkbox, Divider, Space, Row, Col, Flex } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { getPatternWarningLevelColor, getPatternWarningLevelName, PatternWarningLevels } from '../../../../../constants/pattern-warning-levels';
import PatternIcon from '../../../../../components/common/PatternIcon';

/**
 * Component for filtering patterns by warning level and conceptual labels
 */
const FilterPatterns = ({ onApplyFilters }) => {
    // Filter states
    const [warningLevels, setWarningLevels] = useState([]);
    const [hasConceptualLabel, setHasConceptualLabel] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    
    // Warning level options
    const warningLevelOptions = Object.values(PatternWarningLevels).map(level => ({
        label: <Space size={4}><PatternIcon id={level} showId={false} color={getPatternWarningLevelColor(level)} /> {getPatternWarningLevelName(level)}</Space>,
        value: level
    }));
    
    // Conceptual label options
    const conceptualLabelOptions = [
        { label: 'Has conceptual label', value: true },
        { label: 'No conceptual label', value: false }
    ];
    
    // Handle warning level changes (multi-select)
    const handleWarningLevelChange = (checkedValues) => {
        setWarningLevels(checkedValues);
    };
    
    // Handle conceptual label filter changes (single select)
    const handleConceptualLabelChange = (value) => {
        setHasConceptualLabel(value === hasConceptualLabel ? null : value);
    };
    
    // Reset all filters
    const handleResetAll = () => {
        setIsOpen(false);
        onApplyFilters({
            warningLevels: null,
            hasConceptualLabel: null
        });
    };
    
    // Apply the current filters
    const handleApplyFilters = () => {
        onApplyFilters({
            warningLevels: warningLevels.length > 0 ? warningLevels : null,
            hasConceptualLabel
        });
        setIsOpen(false);
    };
    
    // Reset specific filter
    const resetWarningLevels = () => setWarningLevels([]);
    const resetConceptualLabel = () => setHasConceptualLabel(null);

    // Filter content
    const filterContent = (
        <Flex vertical gap={16} style={{ width: '320px' }}>            
            <div>
                <Flex justify="space-between" align="center">
                    <span>Warning Level</span>
                    <Button 
                        type="link" 
                        onClick={resetWarningLevels}
                        disabled={warningLevels.length === 0}
                    >
                        Reset
                    </Button>
                </Flex>
                <Checkbox.Group 
                    options={warningLevelOptions} 
                    value={warningLevels} 
                    onChange={handleWarningLevelChange}
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}
                />
            </div>
            
            <div>
                <Flex justify="space-between" align="center">
                    <span>Conceptual Label</span>
                    <Button 
                        type="link" 
                            onClick={resetConceptualLabel}
                            disabled={hasConceptualLabel === null}
                        >
                            Reset
                        </Button>
                </Flex>
                <Space direction="vertical" style={{ width: '100%', marginTop: '8px' }}>
                    {conceptualLabelOptions.map(option => (
                        <Checkbox
                            key={String(option.value)}
                            checked={hasConceptualLabel === option.value}
                            onChange={() => handleConceptualLabelChange(option.value)}
                        >
                            {option.label}
                        </Checkbox>
                    ))}
                </Space>
            </div>

            <Flex justify="space-between">
                <Button onClick={handleResetAll}>Reset all</Button>
                <Button type="primary" onClick={handleApplyFilters}>Apply now</Button>
            </Flex>
        </Flex>
    );

    return (
        <Popover
            content={filterContent}
            trigger="click"
            placement="bottomRight"
            open={isOpen}
            onOpenChange={setIsOpen}
            title="Filters"
        >
            <Button icon={<FilterOutlined />}>
                Filters
            </Button>
        </Popover>
    );
};

export default FilterPatterns; 